import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/state/AppContext";
import { useAccountDeletion } from "@/lib/useAccountDeletion";
import { Button } from "@/components/ui/button";
import { LEGAL_CONTACT } from "@/constants/legal";

/** Public account-deletion page.
 *
 * Google Play requires a deletion route reachable from the open web by someone who has already
 * uninstalled the app — so this page must render, explain itself and be actionable while signed
 * out. Signed-in users get the button; signed-out users get the instructions and the email route.
 *
 * The deletion itself is `POST /auth/account/delete`, which already existed and already refuses
 * partner accounts and tombstones the identity (principle #1 — this page is a surface on an
 * existing capability, not a second deletion path).
 */

const DELETED = [
  "Your name, mobile number, email address and profile photo",
  "Your Google sign-in link",
  "Your saved addresses",
  "Your favourites and reward points",
  "Your notification settings and push subscriptions",
  "Photos you uploaded",
  "Your name and photo on every review, rating, comment and community post you made",
];

const RETAINED = [
  ["Orders, bookings, appointments and payment records", "8 years — Indian tax law requires businesses to keep records of their transactions"],
  ["Ambulance dispatch records", "8 years, given the nature of the service"],
  ["The text of reviews and posts, with your name removed", "Until you or we delete them — other people have relied on them"],
  ["Security and access logs", "1 year"],
];

export default function DeleteAccount() {
  const { auth } = useApp();
  const [confirming, setConfirming] = useState(false);
  const { remove: removeAccount, busy } = useAccountDeletion();

  const remove = async () => {
    const ok = await removeAccount();
    if (!ok) setConfirming(false);
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14" data-testid="delete-account">
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Delete your account</h1>
      <p className="mt-4 text-base leading-relaxed">
        You can delete your Apna Shahar account and your personal data at any time. There is no
        charge and you do not need to give a reason.
      </p>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">How to delete your account</h2>
        <ul className="mt-3 space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground">
          <li className="list-disc marker:text-primary/60">
            <strong className="text-foreground">In the app:</strong> Account → Settings → Delete
            account. Confirm with the one-time password sent to your mobile number.
          </li>
          <li className="list-disc marker:text-primary/60">
            <strong className="text-foreground">On this website:</strong> sign in, then use the
            button below.
          </li>
          <li className="list-disc marker:text-primary/60">
            <strong className="text-foreground">By email:</strong> write to{" "}
            <span className="font-medium text-foreground">{LEGAL_CONTACT.grievanceEmail}</span> from
            your registered email address, or from any address quoting your registered mobile
            number. We will verify it is you before acting.
          </li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">What is deleted</h2>
        <p className="mt-3 text-[15px] text-muted-foreground">Within 30 days we erase:</p>
        <ul className="mt-3 space-y-2 pl-5 text-[15px] leading-relaxed text-muted-foreground">
          {DELETED.map((item) => (
            <li key={item} className="list-disc marker:text-primary/60">
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">What is kept, and why</h2>
        <p className="mt-3 text-[15px] text-muted-foreground">
          Some records cannot be erased immediately, for reasons outside our control. Everything
          retained is detached from your name and contact details wherever possible.
        </p>
        <dl className="mt-4 space-y-3">
          {RETAINED.map(([what, why]) => (
            <div key={what} className="rounded-lg border px-4 py-3">
              <dt className="text-[15px] font-medium">{what}</dt>
              <dd className="mt-1 text-sm text-muted-foreground">{why}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">If you own a business</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Deleting your personal account does not automatically remove your business listing,
          because customers may have live orders with it. Contact{" "}
          <span className="font-medium text-foreground">{LEGAL_CONTACT.supportEmail}</span> to close
          the listing, and settle any outstanding orders first.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold">Can I change my mind?</h2>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Tell us within 30 days at{" "}
          <span className="font-medium text-foreground">{LEGAL_CONTACT.grievanceEmail}</span> and we
          may be able to restore your account. After 30 days the data is gone and cannot be
          recovered.
        </p>
      </section>

      <div className="mt-12 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
        {!auth ? (
          <>
            <p className="text-[15px]">
              Sign in to delete your account from this website, or email us using the address above.
            </p>
            <Button asChild className="mt-4" variant="outline">
              <Link to="/login">Sign in</Link>
            </Button>
          </>
        ) : !confirming ? (
          <>
            <p className="text-[15px] font-medium">
              This cannot be undone after 30 days.
            </p>
            <Button
              className="mt-4"
              variant="destructive"
              onClick={() => setConfirming(true)}
              data-testid="delete-account-start"
            >
              Delete my account
            </Button>
          </>
        ) : (
          <>
            <p className="text-[15px] font-medium">
              Are you sure? Your account and personal data will be deleted.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                variant="destructive"
                disabled={busy}
                onClick={remove}
                data-testid="delete-account-confirm"
              >
                {busy ? "Deleting…" : "Yes, delete my account"}
              </Button>
              <Button variant="outline" disabled={busy} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>

      <nav className="mt-14 flex flex-wrap gap-x-6 gap-y-2 border-t pt-6 text-sm text-muted-foreground">
        <Link className="hover:text-foreground" to="/privacy">
          Privacy Notice
        </Link>
        <Link className="hover:text-foreground" to="/terms">
          Terms of Service
        </Link>
        <Link className="hover:text-foreground" to="/emergency-disclaimer">
          Emergency Disclaimer
        </Link>
      </nav>
    </div>
  );
}
